import { useNotificationStore } from "../components/Notifications/notification-store";
import { useLoggerStore } from "../logger/logger_store";
import { TFile } from "../models/file-model";
import { TMatter } from "../models/matter-model";
import { TRegionKey } from "../models/region-model";
import { attachFilesToDraft } from "./draft-attachments-service";
import { bestGuessRegion, loadRegionFromBrowser, storeRegionInBrowser } from "./localisation-service";
import {
  loadTokenFromBrowser,
  storeTokenInBrowser,
  SmokeballAuthenticator,
  OAuth2Token,
} from "./smokeball-authenticator";

export enum DialogLoginMessageTypes {
  LoginFailed = "login_failed",
  LoginCompleted = "login_completed",
}

export type TDialogLoginFailed = {
  MessageType: DialogLoginMessageTypes.LoginFailed;
  Error: string;
};

export type TDialogLoginSuccess = {
  MessageType: DialogLoginMessageTypes.LoginCompleted;
  Token: OAuth2Token;
};

const requestTokensFromParent = (asyncResult: Office.AsyncResult<void>) => {
  if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
    console.log("Add ParentMessageReceived event handler succeeded.");
    notifyParentMessageReceivedEventHandlerAdded();
  } else {
    console.log(`Add ParentMessageReceived event handler failed with error: ${asyncResult.error}`);
  }
};

// this will call successCb when the tokens are set in the session, pages should wait for this
// to avoid race conditions where gql is fired before the tokens are present
const childListenForTokens = (successCb: () => void) => (args: Office.DialogParentMessageReceivedEventArgs) => {
  const message = args.message;
  try {
    const { sessionId, interactionId } = JSON.parse(message);
    if (sessionId && interactionId) {
      useLoggerStore.getState().changeInteraction({ sessionId, interactionId });
    } else {
      throw new Error("missing sessionId/interactionId");
    }
  } catch {
    console.warn("could not set up logger, missing sessionId/interactionId");
    // pass ... we tried
  }
  console.log(`Parent message received: ${message}`);
  let messageObject: { token: string; region: TRegionKey };
  try {
    messageObject = JSON.parse(message);
  } catch (error) {
    console.error(`Unable to parse parent message to JSON object with error: ${error}`);
    return;
  }

  if (messageObject.token && messageObject.region) {
    storeTokenInBrowser(messageObject.token, true); // use session storage
    storeRegionInBrowser(messageObject.region, true); // use session store
    console.log("Set Smokeball token to local storage.");
    successCb();
  } else {
    console.log("No Smokeball token or region found from parent message JSON object.");
  }
};

export const childHandshake = (successCb: () => void) => {
  Office.onReady(() => {
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      childListenForTokens(successCb),
      requestTokensFromParent
    );
  }).catch((e) => useNotificationStore.getState().errorNotification("Office failed to become ready", e));
};

export const sendEventToParent = (message: DialogCloseEvent) => {
  Office.context.ui.messageParent(JSON.stringify(message));
};

const sendTokensToChildDialogWhenRequested =
  (dialog: Office.Dialog): Parameters<Office.Dialog["addEventHandler"]>[1] =>
  (args: { message: string; origin: string | undefined } | { error: number }) => {
    const successData = args as { message: string; origin: string | undefined };

    if (successData.message) {
      console.log(`Dialog message received: ${successData.message}`);

      if (isParentMessageReceivedEventhandlerAdded(successData.message)) {
        const token = loadTokenFromBrowser();
        const region = loadRegionFromBrowser();
        const { sessionId, interactionId } = useLoggerStore.getState();
        const message = JSON.stringify({ token, region, sessionId, interactionId });

        if (token) {
          console.log(`Send message to dialog: ${message}`);
          dialog.messageChild(message);
        } else {
          console.log("No Smokeball token to send to the dialog.");
        }
      }
    } else {
      const errorData = args as { error: number };
      console.error(`Error receiving dialog message: ${errorData.error}`);
    }
  };

type TDialogCloseHandler = (dialog: Office.Dialog, closeEvent: DialogCloseEvent) => void;

const childDialogToParentEventHandler =
  (dialog: Office.Dialog, closeHandler: TDialogCloseHandler): Parameters<Office.Dialog["addEventHandler"]>[1] =>
  (event) => {
    if (hasErrorNumber(event)) {
      console.error(`Error receiving dialog message: ${event.error}`);
    } else {
      const closeEvent = getDialogCloseEventFromMessage(event.message);

      if (!closeEvent) {
        return;
      }

      if (closeEvent.type === "CANCEL") {
        console.log("Dialog was cancelled", closeEvent);
        dialog.close();
      } else {
        closeHandler(dialog, closeEvent);
      }
    }
  };

const dialogParentMessageReceivedEventHandlerAddedMessage = "DialogParentMessageReceived event handler added." as const;

export const notifyParentMessageReceivedEventHandlerAdded = (): void => {
  Office.context.ui.messageParent(dialogParentMessageReceivedEventHandlerAddedMessage);
};

const isParentMessageReceivedEventhandlerAdded = (message: string): boolean => {
  return message === dialogParentMessageReceivedEventHandlerAddedMessage;
};

const showDialog = async (route: string, optionsIn?: Office.DialogOptions | undefined): Promise<TDialogResult> => {
  return new Promise((resolve) => {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const url = `${baseUrl}${route}`;

    const options = {
      width: 50,
      height: 50,
      ...optionsIn,
    };

    console.log(`Showing dialog at url: ${url}.`);

    Office.context.ui.displayDialogAsync(url, options, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(makeTDialogResult(result.value, true));
      } else {
        const errorMsg = `Could not open dialog to route: ${route}. Error: ${result.error.code} - ${result.error.name} - ${result.error.message}`;
        console.error(errorMsg);
        useNotificationStore.getState().errorNotification(result.error.name, result.error.code, result.error.message);

        resolve(makeTDialogResult(result.value, false, errorMsg));
      }
    });
  });
};

const showDialogWithHandlers = async (
  path: string,
  closeHandler: TDialogCloseHandler,
  options?: Office.DialogOptions
): Promise<TDialogResult> => {
  const result = await showDialog(path, {
    width: 50,
    height: 50,
    promptBeforeOpen: false,
    ...options,
  });

  if (result.hasError) {
    console.error(`Could not open ${path}...`);

    return result;
  }

  const dialog = result.dialog;

  console.log("Adding DialogMessageReceived event handler.");

  dialog.addEventHandler(Office.EventType.DialogMessageReceived, sendTokensToChildDialogWhenRequested(dialog));

  // look for assign / cancel events
  dialog.addEventHandler(Office.EventType.DialogMessageReceived, childDialogToParentEventHandler(dialog, closeHandler));

  return result;
};

export const showLogin = async () => {
  return new Promise<void>((resolve, reject) => {
    const eventReceivedHandler: Parameters<Office.Dialog["addEventHandler"]>[1] = (args) => {
      if (!("error" in args)) {
        return;
      }

      switch (args.error) {
        case 12002:
          reject(
            "The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid."
          );
          break;
        case 12003:
          reject("The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.");
          break;
        case 12006:
          // 12006 means that the user closed the dialog instead of waiting for it to close.
          // It is not known if the user completed the login or logout, so assume the user is
          // logged out and revert to the app's starting state. It does no harm for a user to
          // press the login button again even if the user is logged in.
          reject("");
          break;
        default:
          reject(`Unknown error while logging in (${args.error}).`);
          break;
      }
    };

    const handleLoginFailure = (message: TDialogLoginFailed) => {
      let errorText = "";

      if (message?.Error) {
        errorText = message.Error;
      }

      reject(errorText);
    };

    const handleLoginSuccess = (message: TDialogLoginSuccess) => {
      try {
        if (!message?.Token) {
          reject("The message Token was not valid...");
          return;
        }

        if (SmokeballAuthenticator.setTokenToStorage(message.Token)) {
          resolve();
        }
      } catch (error) {
        const errorMsg = JSON.stringify(error);

        const dialogMsg: TDialogLoginFailed = {
          Error: errorMsg,
          MessageType: DialogLoginMessageTypes.LoginFailed,
        };

        handleLoginFailure(dialogMsg);
      }
    };

    const handleDialogMessage = (loginDialog: Office.Dialog, messageData: string) => {
      if (!messageData) {
        return;
      }

      const payload = JSON.parse(messageData) as TDialogLoginFailed | TDialogLoginSuccess | undefined;

      if (payload?.MessageType) {
        switch (payload.MessageType) {
          case DialogLoginMessageTypes.LoginCompleted: {
            loginDialog.close();
            handleLoginSuccess(payload);

            return;
          }

          case DialogLoginMessageTypes.LoginFailed: {
            loginDialog.close();
            handleLoginFailure(payload);

            return;
          }

          default: {
            return;
          }
        }
      }
    };

    console.log("launching logging in dialog...");
    showDialog(`/login/login.html?region=${bestGuessRegion().code}`, {
      width: 30,
      height: 40,
      promptBeforeOpen: false,
    })
      .then((result) => {
        if (result.hasError) {
          reject(`Could not open login.`);
          console.error(`Could not open login`);
          return;
        }

        const dialogMessageHandler: Parameters<Office.Dialog["addEventHandler"]>[1] = (args) => {
          let message = "";

          if ("message" in args) {
            message = args.message;
          }

          if (!message) {
            return;
          }

          handleDialogMessage(result.dialog, message);
        };

        result.dialog.addEventHandler(Office.EventType.DialogEventReceived, eventReceivedHandler);
        result.dialog.addEventHandler(Office.EventType.DialogMessageReceived, dialogMessageHandler);
      })
      .catch((e) => reject(e));
  });
};

export const showLogout = () => {
  console.log("Logging out...");
  return showDialog(`/logout/logout.html?region=${bestGuessRegion().code}`, {
    width: 30,
    height: 40,
    promptBeforeOpen: false,
  });
};

export const showAttachFiles = (matterId: string): Promise<TDialogResult> => {
  const closeHandler: TDialogCloseHandler = async (dialog, closeEvent) => {
    if (closeEvent.type === "ATTACH") {
      dialog.close();
      await attachFilesToDraft(closeEvent.files);
    }
  };
  return showDialogWithHandlers(`/attachFiles/${matterId}`, closeHandler);
};

export const showSelectFolder = (
  matterId: string,
  callback: (folderId: string, display: string) => void
): Promise<TDialogResult> => {
  const closeHandler: TDialogCloseHandler = (dialog, closeEvent) => {
    if (closeEvent.type === "SELECT_FOLDER") {
      dialog.close();
      callback(closeEvent.folderId, closeEvent.display); // this cb will run on the main window/taskpane
    }
  };
  return showDialogWithHandlers(`/selectFolder/${matterId}`, closeHandler, { width: 25 });
};

export type DialogCloseEvent =
  | {
      type: "ASSIGN";
      matter: null | TMatter;
    }
  | {
      type: "ATTACH";
      files: TFile[];
    }
  | {
      type: "SELECT_FOLDER";
      folderId: string;
      display: string;
    }
  | {
      type: "CANCEL";
    };

// eslint-disable-next-line
const hasErrorNumber = (thing: any): thing is { error: number } => {
  return thing && typeof thing === "object" && thing.error && typeof thing.error === "number";
};

// eslint-disable-next-line
const isDialogCloseEvent = (thing: any): thing is DialogCloseEvent => {
  return thing && typeof thing === "object" && ["ASSIGN", "CANCEL", "ATTACH", "SELECT_FOLDER"].includes(thing.type);
};

const getDialogCloseEventFromMessage = (message: string): DialogCloseEvent | null => {
  try {
    const parsed = JSON.parse(message) as unknown;
    if (isDialogCloseEvent(parsed)) {
      return parsed;
    }
  } catch {
    // pass
  }

  return null;
};

export type TDialogResult = {
  result: boolean;
  hasError: boolean;
  dialog: Office.Dialog;
  errorMsg: string | null;
};

const makeTDialogResult = (dialog: Office.Dialog, result: boolean, errorMessage?: string | null) => ({
  result: result,
  dialog: dialog,
  hasError: errorMessage != null,
  errorMsg: errorMessage ? errorMessage : null,
});
